import { useLayoutEffect, useRef, type KeyboardEvent } from 'react';
import './EditableText.css';

/**
 * Fields that don't look like fields until you touch them. There is no edit
 * mode in this panel — every one of these is always live, so "click to edit"
 * is just clicking, and the only chrome is a hover tint and the focus ring.
 *
 * Escape blurs rather than reverting. With autosave there is nothing to
 * revert to, and blurring is what lets the second Escape close the panel.
 */

export function EditableLine({
  className,
  value,
  label,
  placeholder,
  inputMode,
  onChange,
  onCommit,
}: {
  className: string;
  value: string;
  label: string;
  placeholder: string;
  inputMode?: 'decimal' | 'url';
  onChange: (next: string) => void;
  onCommit: () => void;
}) {
  return (
    <input
      className={`editable ${className}`}
      value={value}
      aria-label={label}
      placeholder={placeholder}
      inputMode={inputMode}
      spellCheck={false}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onCommit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') event.currentTarget.blur();
      }}
    />
  );
}

export function EditableBlock({
  className,
  value,
  label,
  placeholder,
  onChange,
  onCommit,
}: {
  className: string;
  value: string;
  label: string;
  placeholder: string;
  onChange: (next: string) => void;
  onCommit: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Grows with its content, so the panel has one scrollbar rather than a
  // little one per paragraph. Runs before paint, or the box flickers at its
  // old height between the keystroke and the resize.
  useLayoutEffect(() => {
    const node = ref.current;
    if (node === null) return;

    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }, [value]);

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter is a newline here, unlike the single-line fields above.
    if (event.key === 'Escape') event.currentTarget.blur();
  }

  return (
    <textarea
      ref={ref}
      className={`editable editable--block ${className}`}
      value={value}
      aria-label={label}
      placeholder={placeholder}
      rows={1}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onCommit}
      onKeyDown={onKeyDown}
    />
  );
}
